import type { PreviewUrl, VisualData } from '@adplay/common'
import { type QueryDocumentSnapshot, type SnapshotOptions, Timestamp } from 'firebase/firestore'
import type { MarkOptional, OmitProperties } from 'ts-essentials'
import { getStorageJson, saveStorageJSON } from '~/libs/helper/storage-json'
import compactObject from '~/libs/helper/compactObject'
import type { VisualType } from '~/canvas/core/fabricjs/constants'

export type AdTemplateParams = OmitProperties<MarkOptional<AdTemplate, 'createdAt' | 'updatedAt' >, Function>

export default class AdTemplate {
  id?: string

  title: string

  previewUrls: Array<PreviewUrl>

  videoUrl?: string

  visualData?: VisualData

  visualType?: VisualType

  published: boolean

  featured: boolean

  categoryIds: Array<string>

  isGenerator: boolean = false

  shouldReplaceColors: boolean = true

  prompt?: string

  // metadata
  createdAt?: any
  updatedAt?: any

  static toFirestore(adTemplate: AdTemplate) {
    return compactObject({
      id: adTemplate.id,
      title: adTemplate.title,
      previewUrls: adTemplate.previewUrls,
      videoUrl: adTemplate.videoUrl,
      featured: adTemplate.featured,
      published: adTemplate.published,
      isGenerator: adTemplate.isGenerator,
      categoryIds: adTemplate.categoryIds,
      shouldReplaceColors: adTemplate.shouldReplaceColors ?? true,
      visualType: adTemplate.visualType,
      prompt: adTemplate.prompt,
      createdAt: adTemplate.createdAt ? Timestamp.fromDate(adTemplate.createdAt) : undefined,
      updatedAt: adTemplate.updatedAt ? Timestamp.fromDate(adTemplate.updatedAt) : undefined,
    })
  }

  static fromFirestore(snapshot: QueryDocumentSnapshot | any, options?: SnapshotOptions): AdTemplate {
    const data = snapshot.data()

    return new AdTemplate({
      id: snapshot.id,
      title: data.title,
      previewUrls: data.previewUrls,
      videoUrl: data.videoUrl,
      featured: data.featured,
      published: data.published,
      isGenerator: data.isGenerator,
      visualType: data.visualType,
      prompt: data.prompt,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      shouldReplaceColors: data.shouldReplaceColors ?? true,
      categoryIds: data.categoryIds,
    })
  }

  static getCollectionPath() {
    return 'templates'
  }

  static getDocPath(id: string) {
    if (!id)
      throw 'id is not set'

    return `templates/${id}`
  }

  constructor(params?: AdTemplateParams) {
    this.id = params?.id

    this.title = params?.title || 'Untitled Template'
    this.previewUrls = params?.previewUrls || []
    this.videoUrl = params?.videoUrl

    this.published = params?.published || false
    this.featured = params?.featured || false

    this.visualData = params?.visualData
    this.visualType = params?.visualType
    this.prompt = params?.prompt
    this.createdAt = params?.createdAt
    this.updatedAt = params?.updatedAt
    this.shouldReplaceColors = params?.shouldReplaceColors ?? true
    this.isGenerator = params?.isGenerator || false

    this.categoryIds = params?.categoryIds || []
  }

  async getVisualData(force: boolean = false) {
    if (!this.id)
      throw 'AdTemplate id is not set'

    if (this.visualData && !force)
      return this.visualData

    const contentPath = this.getContentPath()

    const data = await getStorageJson<VisualData>(contentPath)

    if (typeof data?.content === 'string')
      data.content = JSON.parse(data.content)

    this.visualData = data

    return data
  }

  async saveVisualData(data: VisualData) {
    if (!this.id)
      throw 'id is not set'

    const contentPath = this.getContentPath()

    return saveStorageJSON(contentPath, data)
  }

  getContentPath() {
    if (!this.id)
      throw 'AdTemplate id is not set'

    const runtimeConfig = useRuntimeConfig()

    const bucket = runtimeConfig.public.storageBucket

    const contentPath = `gs://${bucket}/templates/${this.id}/content.json`

    return contentPath
  }

  toJSON(): ConstructorParameters<typeof AdTemplate>[0] {
    return {
      id: this.id,
      title: this.title,
      previewUrls: this.previewUrls,
      videoUrl: this.videoUrl,
      featured: this.featured,
      isGenerator: this.isGenerator,
      visualData: this.visualData,
      visualType: this.visualType,
      prompt: this.prompt,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      published: this.published,
      shouldReplaceColors: this.shouldReplaceColors,
      categoryIds: this.categoryIds,
    }
  }
}
